/**
 * Ledger Service - Frontend service for ledger operations
 * Epic 4.5: Read-only Ledger Views API
 */

import api from "@/shared/api.jsx";

/**
 * Get ledger entries for a job
 * @param {string} jobId - Job ID
 * @param {Object} options - Query options
 * @param {Date|string} options.asOfDate - As of date
 * @param {number} options.limit - Limit
 * @param {string} options.entryType - Entry type filter
 * @returns {Promise<Object>} Ledger entries
 */
export const getJobLedgerEntries = async (jobId, options = {}) => {
  try {
    const params = {};
    if (options.asOfDate) {
      params.asOfDate = options.asOfDate instanceof Date 
        ? options.asOfDate.toISOString() 
        : options.asOfDate;
    }
    if (options.limit) params.limit = options.limit;
    if (options.entryType) params.entryType = options.entryType;

    const response = await api.get(`/ledger/jobs/${jobId}/entries`, { params });
    return {
      success: true,
      data: response.data || response,
      count: response.count || 0,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0,
    };
  }
};

/**
 * Get financial summary for a job
 * @param {string} jobId - Job ID
 * @param {Date|string} asOfDate - As of date
 * @returns {Promise<Object>} Financial summary
 */
export const getJobLedgerSummary = async (jobId, asOfDate) => {
  try {
    const params = {};
    if (asOfDate) {
      params.asOfDate = asOfDate instanceof Date 
        ? asOfDate.toISOString() 
        : asOfDate;
    }

    const response = await api.get(`/ledger/jobs/${jobId}/summary`, { params });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get ledger entries for a customer
 * @param {string} customerId - Customer ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Ledger entries
 */
export const getCustomerLedgerEntries = async (customerId, options = {}) => {
  try {
    const params = {};
    if (options.asOfDate) {
      params.asOfDate = options.asOfDate instanceof Date 
        ? options.asOfDate.toISOString() 
        : options.asOfDate;
    }
    if (options.limit) params.limit = options.limit;
    if (options.entryType) params.entryType = options.entryType;

    const response = await api.get(`/ledger/customers/${customerId}/entries`, { params });
    return {
      success: true,
      data: response.data || response,
      count: response.count || 0,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0,
    };
  }
};

/**
 * Get financial summary for a customer
 * @param {string} customerId - Customer ID
 * @param {Date|string} asOfDate - As of date
 * @returns {Promise<Object>} Financial summary
 */
export const getCustomerLedgerSummary = async (customerId, asOfDate) => {
  try {
    const params = {};
    if (asOfDate) {
      params.asOfDate = asOfDate instanceof Date 
        ? asOfDate.toISOString() 
        : asOfDate;
    }

    const response = await api.get(`/ledger/customers/${customerId}/summary`, { params });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get outstanding balance for a customer
 * @param {string} customerId - Customer ID
 * @param {Date|string} asOfDate - As of date
 * @returns {Promise<Object>} Outstanding balance
 */
export const getCustomerOutstandingBalance = async (customerId, asOfDate) => {
  try {
    const params = {};
    if (asOfDate) {
      params.asOfDate = asOfDate instanceof Date 
        ? asOfDate.toISOString() 
        : asOfDate;
    }

    const response = await api.get(`/ledger/customers/${customerId}/outstanding`, { params });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get ledger entries for an invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Ledger entries
 */
export const getInvoiceLedgerEntries = async (invoiceId, options = {}) => {
  try {
    const params = {};
    if (options.asOfDate) {
      params.asOfDate = options.asOfDate instanceof Date 
        ? options.asOfDate.toISOString() 
        : options.asOfDate;
    }
    if (options.limit) params.limit = options.limit;
    if (options.entryType) params.entryType = options.entryType;

    const response = await api.get(`/ledger/invoices/${invoiceId}/entries`, { params });
    return {
      success: true,
      data: response.data || response,
      count: response.count || 0,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0,
    };
  }
};

/**
 * Get ledger entries for a provider/payee
 * @param {string} providerId - Provider ID
 * @param {Object} options - Query options
 * @param {Date|string} options.asOfDate - As of date
 * @param {number} options.limit - Limit
 * @param {string} options.entryType - Entry type filter
 * @returns {Promise<Object>} Ledger entries
 */
export const getProviderLedgerEntries = async (providerId, options = {}) => {
  try {
    const params = {};
    if (options.asOfDate) {
      params.asOfDate = options.asOfDate instanceof Date 
        ? options.asOfDate.toISOString() 
        : options.asOfDate;
    }
    if (options.limit) params.limit = options.limit;
    if (options.entryType) params.entryType = options.entryType;

    const response = await api.get(`/ledger/providers/${providerId}/entries`, { params });
    return {
      success: true,
      data: response.data || response,
      count: response.count || 0,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0,
    };
  }
};

/**
 * Get financial summary for a provider/payee
 * @param {string} providerId - Provider ID
 * @param {Date|string} asOfDate - As of date
 * @returns {Promise<Object>} Financial summary
 */
export const getProviderLedgerSummary = async (providerId, asOfDate) => {
  try {
    const params = {};
    if (asOfDate) {
      params.asOfDate = asOfDate instanceof Date 
        ? asOfDate.toISOString() 
        : asOfDate;
    }

    const response = await api.get(`/ledger/providers/${providerId}/summary`, { params });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get job ledger summary (alias for getJobLedgerSummary with consistent naming)
 * @param {string} jobId - Job ID
 * @param {Date|string} [asOfDate] - Optional cutoff date (ISO string or Date)
 * @returns {Promise<Object>} Summary data
 */
export const getJobSummary = async (jobId, asOfDate = null) => {
  return getJobLedgerSummary(jobId, asOfDate);
};
